import { useState, useEffect, useCallback } from "react"
import {
  listNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  type NotificationRow,
} from "@/lib/supabase"

/**
 * Hook centralisé pour les notifications.
 *
 * Fournit :
 * - notifications : liste triée récent → ancien
 * - unreadCount   : compteur pour le badge
 * - loading       : indicateur de chargement initial
 * - markRead      : marque UNE notif lue (optimistic + sync)
 * - markAllRead   : marque tout lu (optimistic + sync)
 * - remove        : supprime une notif (optimistic + sync)
 * - refresh       : refetch manuel
 *
 * Utilisé par :
 * - NotificationsPage (liste complète + actions)
 * - DashboardHome     (badge unreadCount uniquement)
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationRow[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const [notifs, count] = await Promise.all([
        listNotifications(),
        getUnreadNotificationCount(),
      ])
      setNotifications(notifs)
      setUnreadCount(count)
    } catch (e) {
      console.error("[useNotifications] refresh error:", e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const markRead = useCallback(async (id: string) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id && !n.read ? { ...n, read: true } : n))
    )
    setUnreadCount((c) => Math.max(0, c - 1))

    try {
      await markNotificationRead(id)
    } catch (e) {
      console.error("[useNotifications] markRead error:", e)
      refresh() // rollback via refetch
    }
  }, [refresh])

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
    setUnreadCount(0)

    try {
      await markAllNotificationsRead()
    } catch (e) {
      console.error("[useNotifications] markAllRead error:", e)
      refresh()
    }
  }, [refresh])

  const remove = useCallback(async (id: string) => {
    const target = notifications.find((n) => n.id === id)
    setNotifications((prev) => prev.filter((n) => n.id !== id))
    if (target && !target.read) setUnreadCount((c) => Math.max(0, c - 1))

    try {
      await deleteNotification(id)
    } catch (e) {
      console.error("[useNotifications] remove error:", e)
      refresh()
    }
  }, [notifications, refresh])

  return { notifications, unreadCount, loading, markRead, markAllRead, remove, refresh }
}
